/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CaptionStyleDto,
  CreateCaptionStyleDto,
  UpdateCaptionStyleDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The workspace's own caption styles, oldest first, with how many flows use each.
 */
export const listCaptionStyles = (id: string, signal?: AbortSignal) => {
  return customInstance<CaptionStyleDto[]>({
    url: `/api/v1/workspaces/${id}/caption-styles`,
    method: 'GET',
    signal,
  });
};

export const getListCaptionStylesQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/caption-styles`] as const;
};

export const getListCaptionStylesQueryOptions = <
  TData = Awaited<ReturnType<typeof listCaptionStyles>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCaptionStyles>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCaptionStylesQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCaptionStyles>>> = ({
    signal,
  }) => listCaptionStyles(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCaptionStyles>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListCaptionStylesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCaptionStyles>>
>;
export type ListCaptionStylesQueryError = ErrorType<ApiErrorDto>;

export function useListCaptionStyles<
  TData = Awaited<ReturnType<typeof listCaptionStyles>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCaptionStyles>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCaptionStyles>>,
          TError,
          Awaited<ReturnType<typeof listCaptionStyles>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCaptionStyles<
  TData = Awaited<ReturnType<typeof listCaptionStyles>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCaptionStyles>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCaptionStyles>>,
          TError,
          Awaited<ReturnType<typeof listCaptionStyles>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCaptionStyles<
  TData = Awaited<ReturnType<typeof listCaptionStyles>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCaptionStyles>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The workspace's own caption styles, oldest first, with how many flows use each.
 */

export function useListCaptionStyles<
  TData = Awaited<ReturnType<typeof listCaptionStyles>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listCaptionStyles>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCaptionStylesQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save a new caption style. Owners and editors.
 */
export const createCaptionStyle = (
  id: string,
  createCaptionStyleDto: BodyType<CreateCaptionStyleDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CaptionStyleDto>({
    url: `/api/v1/workspaces/${id}/caption-styles`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createCaptionStyleDto,
    signal,
  });
};

export const getCreateCaptionStyleMutationKey = () => ['createCaptionStyle'] as const;

export const getCreateCaptionStyleMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCaptionStyle>>,
    TError,
    CreateCaptionStyleMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCaptionStyle>>,
  TError,
  CreateCaptionStyleMutationVariables,
  TContext
> => {
  const mutationKey = getCreateCaptionStyleMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCaptionStyle>>,
    CreateCaptionStyleMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createCaptionStyle(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCaptionStyleMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCaptionStyle>>
>;
export type CreateCaptionStyleMutationBody = BodyType<CreateCaptionStyleDto>;
export type CreateCaptionStyleMutationError = ErrorType<ApiErrorDto>;
export type CreateCaptionStyleMutationVariables = {
  id: string;
  data: BodyType<CreateCaptionStyleDto>;
};

/**
 * @summary Save a new caption style. Owners and editors.
 */
export const useCreateCaptionStyle = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createCaptionStyle>>,
      TError,
      CreateCaptionStyleMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createCaptionStyle>>,
  TError,
  CreateCaptionStyleMutationVariables,
  TContext
> => {
  return useMutation(getCreateCaptionStyleMutationOptions(options), queryClient);
};
/**
 * @summary Rename a caption style or change how it looks; flows using it pick up the change.
 */
export const updateCaptionStyle = (
  id: string,
  styleId: string,
  updateCaptionStyleDto: BodyType<UpdateCaptionStyleDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CaptionStyleDto>({
    url: `/api/v1/workspaces/${id}/caption-styles/${styleId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateCaptionStyleDto,
    signal,
  });
};

export const getUpdateCaptionStyleMutationKey = () => ['updateCaptionStyle'] as const;

export const getUpdateCaptionStyleMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateCaptionStyle>>,
    TError,
    UpdateCaptionStyleMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateCaptionStyle>>,
  TError,
  UpdateCaptionStyleMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateCaptionStyleMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateCaptionStyle>>,
    UpdateCaptionStyleMutationVariables
  > = (props) => {
    const { id, styleId, data } = props ?? {};

    return updateCaptionStyle(id, styleId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCaptionStyleMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateCaptionStyle>>
>;
export type UpdateCaptionStyleMutationBody = BodyType<UpdateCaptionStyleDto>;
export type UpdateCaptionStyleMutationError = ErrorType<ApiErrorDto>;
export type UpdateCaptionStyleMutationVariables = {
  id: string;
  styleId: string;
  data: BodyType<UpdateCaptionStyleDto>;
};

/**
 * @summary Rename a caption style or change how it looks; flows using it pick up the change.
 */
export const useUpdateCaptionStyle = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateCaptionStyle>>,
      TError,
      UpdateCaptionStyleMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateCaptionStyle>>,
  TError,
  UpdateCaptionStyleMutationVariables,
  TContext
> => {
  return useMutation(getUpdateCaptionStyleMutationOptions(options), queryClient);
};
/**
 * @summary Delete a caption style. Flows that used it fall back to the workspace default.
 */
export const deleteCaptionStyle = (id: string, styleId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/caption-styles/${styleId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteCaptionStyleMutationKey = () => ['deleteCaptionStyle'] as const;

export const getDeleteCaptionStyleMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCaptionStyle>>,
    TError,
    DeleteCaptionStyleMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCaptionStyle>>,
  TError,
  DeleteCaptionStyleMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteCaptionStyleMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCaptionStyle>>,
    DeleteCaptionStyleMutationVariables
  > = (props) => {
    const { id, styleId } = props ?? {};

    return deleteCaptionStyle(id, styleId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCaptionStyleMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteCaptionStyle>>
>;

export type DeleteCaptionStyleMutationError = ErrorType<ApiErrorDto>;
export type DeleteCaptionStyleMutationVariables = { id: string; styleId: string };

/**
 * @summary Delete a caption style. Flows that used it fall back to the workspace default.
 */
export const useDeleteCaptionStyle = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteCaptionStyle>>,
      TError,
      DeleteCaptionStyleMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteCaptionStyle>>,
  TError,
  DeleteCaptionStyleMutationVariables,
  TContext
> => {
  return useMutation(getDeleteCaptionStyleMutationOptions(options), queryClient);
};
